import type {
  ApiLimitTypeForPromo, ApiPremiumSection, ApiReactionEmoji,
} from './api/types';
import type {
  GiftProfileFilterOptions,
  ResaleGiftsFilterOptions,
} from './types';

export const APP_CODE_NAME = 'A';
export const APP_NAME = process.env.APP_NAME || 'The Advance Chat';

// The web app's own public origin — used to recognize and build self-referential share links
// (chat/message/invite links) and to detect whether the current tab is on that origin. Falls back
// to the product's own domain only if unset (webpack always sets it at build time).
export const BASE_URL = process.env.BASE_URL;
export const PRODUCTION_URL = BASE_URL || 'https://m.theadvance.com';
export const PRODUCTION_HOSTNAME = new URL(PRODUCTION_URL).hostname;
export const WEB_VERSION_BASE = `${PRODUCTION_URL}/`; // Used to redirect to other versions
export const ACCOUNT_QUERY = 'account';

export const IS_MOCKED_CLIENT = process.env.APP_MOCKED_CLIENT === '1';
// `APP_ENV === 'test'` is overloaded: the Jest/Playwright harness sets it, but so does the `test`
// DEPLOY tier (the devtunnel) — which is a real running app. Only treat it as the test harness when
// we're actually under the test runner (jsdom `NODE_ENV`) or the mocked Playwright client. Otherwise
// the `test` tier would disable media workers / service worker / notifications and crash (e.g.
// RLottie does `workers[i].request(...)` on an empty worker pool).
export const IS_TEST = process.env.APP_ENV === 'test'
  && (process.env.NODE_ENV === 'test' || IS_MOCKED_CLIENT);
export const IS_PERF = process.env.APP_ENV === 'perf';
export const IS_BETA = process.env.APP_ENV === 'staging';

export const PAID_MESSAGES_PURPOSE = 'paid_messages';

export const DEBUG = process.env.APP_ENV !== 'production';
export const DEBUG_MORE = false;
export const DEBUG_CALLS = true;
export const DEBUG_LOG_FILENAME = 'tt-log.json';
export const STRICTERDOM_ENABLED = DEBUG;
export const FORCE_FALLBACK_LANG = DEBUG;

export const BETA_CHANGELOG_URL = `${PRODUCTION_URL}/changelog`;

export const DEBUG_ALERT_MSG = 'Shoot!\nSomething went wrong, please see the error details in Dev Tools Console.';
export const DEBUG_GRAMJS = false;

export const PAGE_TITLE = process.env.APP_TITLE!;
export const PAGE_TITLE_TAURI = 'The Advance Chat';
export const INACTIVE_MARKER = '[Inactive]';

export const DEBUG_PAYMENT_SMART_GLOCAL = false;

export const SESSION_LEGACY_USER_KEY = 'user_auth';
export const SESSION_ACCOUNT_PREFIX = 'account';
// Which account slot the user last had in front of them. `localStorage` belongs to the origin, not to
// an account — slots only namespace key names — so this single entry is readable from any tab.
export const ACTIVE_ACCOUNT_SLOT_KEY = 'tt-active-account-slot';
export const LEGACY_PASSCODE_CACHE_NAME = 'tt-passcode';
// One passcode covers every account on the device, so its encrypted blobs live in a single database
// (keyed per account slot inside it) and this flag records that a passcode exists at all — an account
// that never set one still has to lock, and its settings screen still has to show it as enabled.
export const PASSCODE_IDB_STORE_NAME = 'tt-passcode';
export const PASSCODE_ENABLED_CACHE_KEY = 'tt-has-passcode';
// The REST provider's bearer-token session, kept in the default `idb-keyval` store so both the API
// worker (which writes it) and the main thread (passcode lock, sign-out) can reach it. One entry per
// account slot — always build the key through this prefix, never inline it, or the readers drift
// apart and silently operate on a key nobody writes.
export const REST_SESSION_IDB_KEY_PREFIX = 'rest-session-';

export const MULTIACCOUNT_MAX_SLOTS = 6;
export const GLOBAL_STATE_CACHE_DISABLED = false;
export const GLOBAL_STATE_CACHE_PREFIX = 'tt-global-state-v5';
export const SHARED_STATE_CACHE_KEY = 'tt-shared-state';
export const GLOBAL_STATE_CACHE_USER_LIST_LIMIT = 500;
export const GLOBAL_STATE_CACHE_CHAT_LIST_LIMIT = 200;
export const GLOBAL_STATE_CACHE_ARCHIVED_CHAT_LIST_LIMIT = 10;
export const GLOBAL_STATE_CACHE_CUSTOM_EMOJI_LIMIT = 150;
// How many installed sets keep their sticker list in the persisted state. Set metadata is kept for
// all of them (it is a few hundred bytes each); the sticker lists are what grow, and a platform
// whose default catalogue is dozens of sets would otherwise write megabytes of them on every flush.
// Sets past the limit re-fetch on demand, which is what an uncached set does anyway.
export const GLOBAL_STATE_CACHE_STICKER_SET_LIMIT = 8;

// Message history is persisted per chat in its own IndexedDB database, apart from the global-state
// blob — which is rewritten in full on every cache tick and therefore cannot grow with history. These
// bound what that store keeps: without them it would grow until the origin crossed its storage quota,
// at which point the browser evicts every cache it owns, not just this one.
export const MESSAGE_STORE_CHAT_LIMIT = 40;
export const MESSAGE_STORE_MESSAGE_LIMIT = 200;

export const IS_SCREEN_LOCKED_CACHE_KEY = 'tt-is-screen-locked';
// Device-wide voice/music speed: outlives sign-out and is shared by every account on this browser
export const AUDIO_PLAYBACK_RATE_CACHE_KEY = 'tt-audio-playback-rate';

export const MEDIA_CACHE_DISABLED = false;
export const MEDIA_CACHE_NAME = 'tt-media';
// Service-worker-managed Cache Storage for native `<img>` media-proxy responses. Persists large
// message photos that the browser HTTP cache evicts (the proxy streams them without `Content-Length`,
// so they only live in the volatile in-memory cache and re-download on every chat switch).
// `_2` stores CORS responses with their real byte size stamped on them. The first version stored
// OPAQUE ones, and the browser charges an opaque Cache Storage entry a large random padding against
// the origin quota: measured on production at 14.7 MB per 30 KB image, against 0.03 MB for the same
// image stored as CORS — 1.5 GB of quota for 98 MB of media. Renaming reclaims that in one step
// instead of waiting for ~600 entries to rotate out through the FIFO trim.
// The suffix is `-v2`, not `_2`: `getCacheName` builds per-account names as `<base>_<slot>`, so an
// underscore here would read as "slot 2" to every cache that scans those names.
export const MEDIA_CACHE_NAME_SW = 'tt-media-sw-v2';
export const LEGACY_MEDIA_CACHE_NAMES_SW = ['tt-media-sw'];
export const MEDIA_CACHE_NAME_AVATARS = 'tt-media-avatars';
export const MEDIA_PROGRESSIVE_CACHE_DISABLED = false;
export const MEDIA_PROGRESSIVE_CACHE_NAME = 'tt-media-progressive';
// Persisted last-rendered frames of animated media (stickers, dice, GIFs, videos), so a reload can
// paint the frame the user last saw instead of a blank box while the players re-decode.
export const MEDIA_FRAME_CACHE_NAME = 'tt-media-frames';
export const MEDIA_CACHE_MAX_BYTES = 512 * 1024; // 512 KB
// Stamped onto every entry of the app-managed media caches so the hourly cleanup can age and size
// them from headers alone, instead of reading ~1 GB of bodies back off disk. Shared with the service
// worker, which writes progressive video parts into one of those same caches and must stamp them too
// — an unstamped entry reads as "never accessed", which exempts it from the TTL and sorts it to the
// front of the eviction queue.
export const MEDIA_CACHE_LAST_ACCESS_HEADER = 'X-Last-Access';
export const MEDIA_CACHE_SIZE_HEADER = 'X-Size';
// How long message media may take to resolve before its loading spinner is shown. A cached photo or
// video comes back from Cache Storage well inside this window, so revisiting a chat or reloading no
// longer flashes a spinner over media the user already has.
export const MEDIA_SPINNER_DELAY = 400;
export const CUSTOM_BG_CACHE_NAME = 'tt-custom-bg';
// Bump on every edit to the bundled OLD-lang pack (`VI_OLD_OVERRIDES`). `oldSetLanguage` reads this
// cache with no version check — a cached pack is used forever — so `VI_PACK_VERSION` alone reaches
// only the new-lang system, and old-lang keys (the emoji picker category headers among them) keep
// rendering their raw key on clients that already cached an older pack.
// Shared with the service worker, which deletes every cache carrying this prefix except the current
// version — bumping the version below used to leave the old cache on disk forever.
// Bump this AFTER the pack edits are finished, and bump it again for every later edit: a client that
// loads the app mid-edit caches the half-finished pack under the current name and never refetches it.
export const LANG_CACHE_NAME_PREFIX = 'tt-lang-packs-v';
export const LANG_CACHE_NAME = `${LANG_CACHE_NAME_PREFIX}94`;
export const ASSET_CACHE_NAME = 'tt-assets';
// Build assets whose contents can never change for a given url: the emoji sprites (their filename is
// the Unicode codepoint), the rlottie/opus wasm binaries and the notification sounds. They live apart
// from `ASSET_CACHE_NAME` precisely so the per-release `clearAssetCache()` does not wipe them — the
// emoji picker alone fetches ~300 files per open, and prod serves them with no `Cache-Control`, so
// re-downloading them after every release (or after every hard reload, which re-registers the worker)
// is what made the picker render broken images.
export const STATIC_CACHE_NAME = 'tt-static';
// Byte ceiling for the service-worker media cache. Counting entries is not enough: 600 message photos
// is a few hundred MB, and once the origin exceeds its storage quota the browser evicts the WHOLE
// origin bucket — taking the emoji cache with it.
export const MEDIA_CACHE_SW_MAX_BYTES = 300 * 1024 * 1024; // 300 MB
// Byte ceilings for the app-managed media caches, which until now were bounded only by a 5-day TTL.
// Five days of an active account measured 1.2 GB on one machine — and with multiple accounts each
// slot keeps its own copy of every bucket, so the real figure is that times the number of accounts.
export const MEDIA_CACHE_MAX_BYTES_TOTAL = 200 * 1024 * 1024; // 200 MB
export const MEDIA_PROGRESSIVE_CACHE_MAX_BYTES = 300 * 1024 * 1024; // 300 MB
// Largest file the progressive cache keeps in full. Past this offset a part is streamed but not
// stored, so one huge file cannot evict every other video on its own; it still replays its opening
// from cache. Sized so an ordinary phone recording (measured 42 MB for 1:19) is kept whole.
export const MEDIA_PROGRESSIVE_MAX_CACHED_FILE_BYTES = 64 * 1024 * 1024; // 64 MB
export const MEDIA_CACHE_AVATARS_MAX_BYTES = 20 * 1024 * 1024; // 20 MB
export const AUTODOWNLOAD_FILESIZE_MB_LIMITS = [1, 5, 10, 50, 100, 500];
export const DATA_BROADCAST_CHANNEL_PREFIX = 'tt-global';
export const ESTABLISH_BROADCAST_CHANNEL_PREFIX = 'tt-establish';
export const MULTITAB_LOCALSTORAGE_KEY_PREFIX = 'tt-multitab';
export const INTERCLIENT_BROADCAST_CHANNEL = 'tgweb';
export const DC_IDS = [1, 2, 3, 4, 5] as const;

export const DOWNLOAD_WORKERS = 16;
export const UPLOAD_WORKERS = 16;

const isBigScreen = typeof window !== 'undefined' && window.innerHeight >= 900;

export const MIN_PASSWORD_LENGTH = 1;

export const MESSAGE_LIST_SLICE = isBigScreen ? 60 : 40;
export const MESSAGE_LIST_VIEWPORT_LIMIT = MESSAGE_LIST_SLICE * 2;

export const ARCHIVE_MINIMIZED_HEIGHT = 36;
export const CHAT_HEIGHT_PX = 72;
export const TOPIC_HEIGHT_PX = 65;
export const PEER_PICKER_ITEM_HEIGHT_PX = 56;
export const CHAT_LIST_SLICE = isBigScreen ? 30 : 25;
export const CHAT_LIST_LOAD_SLICE = 100;
export const SHARED_MEDIA_SLICE = 42;
export const CHAT_MEDIA_SLICE = 42;
export const MESSAGE_SEARCH_SLICE = 42;
export const GLOBAL_SEARCH_SLICE = 20;
export const GLOBAL_TOPIC_SEARCH_SLICE = 5;
export const MEMBERS_SLICE = 30;
export const MEMBERS_LOAD_SLICE = 200;
export const CHAT_ONLINES_REFRESH_INTERVAL = 60000;
export const PROFILE_SENSITIVE_AREA = 500;
export const TOPIC_LIST_SENSITIVE_AREA = 600;

// Public Posts Search defaults
export const PUBLIC_POSTS_SEARCH_DEFAULT_STARS_AMOUNT = 10;
export const PUBLIC_POSTS_SEARCH_DEFAULT_TOTAL_DAILY = 2;

// Suggested Posts defaults
export const TON_CURRENCY_CODE = 'TON';
export const VERIFY_AGE_MIN_DEFAULT = 18;

export const STORY_VIEWS_MIN_SEARCH = 15;
export const STORY_MIN_REACTIONS_SORT = 10;
export const STORY_VIEWS_MIN_CONTACTS_FILTER = 20;

export const MEDIA_TIMESTAMP_SAVE_MINIMUM_DURATION = 30; // 30s

export const GLOBAL_SUGGESTED_CHANNELS_ID = 'global';

// As in TheAdvance for Android
// https://github.com/DrKLO/Telegram/blob/51e9947527/TMessagesProj/src/main/java/org/telegram/messenger/MediaDataController.java#L7781
export const REACTION_UNREAD_SLICE = 100;
export const MENTION_UNREAD_SLICE = 100;
export const POLL_UNREAD_SLICE = 100;
export const TOPICS_SLICE = 20;
export const TOPICS_SLICE_SECOND_LOAD = 500;

export const TOP_CHAT_MESSAGES_PRELOAD_LIMIT = 20;

export const SPONSORED_MESSAGE_CACHE_MS = 300000; // 5 min

export const DEFAULT_CHARGE_FOR_MESSAGES = 250;
export const MINIMUM_CHARGE_FOR_MESSAGES = 1;
export const DEFAULT_MAXIMUM_CHARGE_FOR_MESSAGES = 10000;

// The palette the composer offers for colouring a run of text, and the only shades this client
// sends. The convention carries a literal `#RRGGBB`, so these values travel inside the message and
// every platform paints the same thing — which is also why they are fixed here rather than derived
// from the theme.
export const TEXT_COLORS = [
  '#E53935', // red
  '#F57C00', // orange
  '#F9A825', // yellow
  '#43A047', // green
  '#1E88E5', // blue
  '#8E24AA', // purple
  '#D81B60', // pink
] as const;

// Anything else is not a colour this client will paint. The backend accepts entities without looking
// at them, so a malformed shade can reach us from a buggy client or a direct API call — every read
// is checked against this before it becomes a style.
export const TEXT_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export const DEFAULT_VOLUME = 1;
export const DEFAULT_PLAYBACK_RATE = 1;
export const PLAYBACK_RATE_FOR_AUDIO_MIN_DURATION = 0 * 60; // 0

export const ANIMATION_LEVEL_CUSTOM = -1;
export const ANIMATION_LEVEL_MIN = 0;
export const ANIMATION_LEVEL_MED = 1;
export const ANIMATION_LEVEL_MAX = 2;
export const ANIMATION_LEVEL_DEFAULT = ANIMATION_LEVEL_MED;

export const DEFAULT_MESSAGE_TEXT_SIZE_PX = 16;
export const IOS_DEFAULT_MESSAGE_TEXT_SIZE_PX = 17;
export const MACOS_DEFAULT_MESSAGE_TEXT_SIZE_PX = 15;

export const NBSP = '\u00A0';
export const NNBSP = '\u202F';

export const FOLDERS_POSITION_TOP = 'top';
export const FOLDERS_POSITION_LEFT = 'left';
export const FOLDERS_POSITION_DEFAULT = FOLDERS_POSITION_TOP;

export const PREVIEW_AVATAR_COUNT = 3;

export const DRAFT_DEBOUNCE = 10000; // 10s
export const SEND_MESSAGE_ACTION_INTERVAL = 3000; // 3s
// 10000s refetch window for the app config.
export const APP_CONFIG_REFETCH_INTERVAL = 10000 * 1000;
export const GENERAL_REFETCH_INTERVAL = 60 * 60 * 1000; // 1h

export const EDITABLE_INPUT_ID = 'editable-message-text';
export const EDITABLE_INPUT_MODAL_ID = 'editable-message-text-modal';
export const EDITABLE_STORY_INPUT_ID = 'editable-story-input-text';
// eslint-disable-next-line @stylistic/max-len
export const EDITABLE_INPUT_CSS_SELECTOR = `.messages-layout .Transition_slide-active #${EDITABLE_INPUT_ID}, .messages-layout .Transition > .Transition_slide-to #${EDITABLE_INPUT_ID}`;
export const EDITABLE_INPUT_MODAL_CSS_SELECTOR = `#${EDITABLE_INPUT_MODAL_ID}`;
export const EDITABLE_STORY_INPUT_CSS_SELECTOR = `#${EDITABLE_STORY_INPUT_ID}`;

export const CUSTOM_APPENDIX_ATTRIBUTE = 'data-has-custom-appendix';
export const MESSAGE_CONTENT_CLASS_NAME = 'message-content';
export const MESSAGE_CONTENT_SELECTOR = '.message-content';
export const VT_CLASS_NAME = 'active-view-transition';
export const VT_TYPE_CLASS_PREFIX = 'active-vt-';

export const RESIZE_HANDLE_CLASS_NAME = 'resizeHandle';
export const RESIZE_HANDLE_SELECTOR = `.${RESIZE_HANDLE_CLASS_NAME}`;

export const SNAP_EFFECT_CONTAINER_ID = 'snap-effect-container';
export const SNAP_EFFECT_ID = 'snap-effect';

export const STARS_ICON_PLACEHOLDER = '⭐';
export const STARS_CURRENCY_CODE = 'XTR';

export const MIN_SCREEN_WIDTH_FOR_STATIC_RIGHT_COLUMN = 1275; // px
export const MIN_SCREEN_WIDTH_FOR_STATIC_LEFT_COLUMN = 925; // px
export const MOBILE_SCREEN_MAX_WIDTH = 600; // px
export const MOBILE_SCREEN_LANDSCAPE_MAX_WIDTH = 950; // px
export const MOBILE_SCREEN_LANDSCAPE_MAX_HEIGHT = 450; // px

export const MAX_INT_32 = 2 ** 31 - 1;
export const TMP_CHAT_ID = '0';

export const MUTE_INDEFINITE_TIMESTAMP = MAX_INT_32;
export const UNMUTE_TIMESTAMP = 0;

export const ANIMATION_END_DELAY = 100;
export const ANIMATION_WAVE_MIN_INTERVAL = 200;
export const MESSAGE_APPEARANCE_DELAY = 10;
export const PAID_SEND_DELAY = 5000;

export const SCROLL_MIN_DURATION = 300;
export const SCROLL_MAX_DURATION = 600;
export const SCROLL_MAX_DISTANCE = 800;
export const SCROLL_SHORT_TRANSITION_MAX_DISTANCE = 300; // px

// Average duration of message sending animation
export const API_UPDATE_THROTTLE = Math.round((SCROLL_MIN_DURATION + SCROLL_MAX_DURATION) / 2);
export const API_THROTTLE_RESET_UPDATES = new Set([
  'newMessage',
  'newScheduledMessage',
  'deleteMessages',
  'deleteScheduledMessages',
  'deleteHistory',
  'deleteParticipantHistory',
]);

export const LOCK_SCREEN_ANIMATION_DURATION_MS = 200;

export const STICKER_SIZE_INLINE_DESKTOP_FACTOR = 13;
export const STICKER_SIZE_INLINE_MOBILE_FACTOR = 11;
export const STICKER_SIZE_AUTH = 160;
export const STICKER_SIZE_AUTH_MOBILE = 120;
export const STICKER_SIZE_PICKER = 72;
export const EMOJI_SIZE_PICKER = 36;
export const COMPOSER_EMOJI_SIZE_PICKER = 32;
export const STICKER_SIZE_GENERAL_SETTINGS = 40;
export const STICKER_SIZE_PICKER_HEADER = 32;
export const STICKER_PICKER_MAX_SHARED_COVERS = 20;
export const STICKER_SIZE_SEARCH = 72;
export const STICKER_SIZE_MODAL = 72;
export const EMOJI_SIZE_MODAL = 36;
export const STICKER_SIZE_TWO_FA = 160;
export const STICKER_SIZE_PASSCODE = 160;
export const STICKER_SIZE_DISCUSSION_GROUPS = 140;
export const STICKER_SIZE_FOLDER_SETTINGS = 100;
export const STICKER_SIZE_INLINE_BOT_RESULT = 100;
export const STICKER_SIZE_JOIN_REQUESTS = 140;
export const STICKER_SIZE_INVITES = 140;
export const RECENT_STICKERS_LIMIT = 20;
export const RECENT_STATUS_LIMIT = 20;
export const EMOJI_STATUS_LOOP_LIMIT = 2;
export const EMOJI_SIZES = 7;
export const TOP_SYMBOL_SET_ID = 'top';
export const POPULAR_SYMBOL_SET_ID = 'popular';
export const RECENT_SYMBOL_SET_ID = 'recent';
export const COLLECTIBLE_STATUS_SET_ID = 'collectibleStatus';
export const FAVORITE_SYMBOL_SET_ID = 'favorite';
export const EFFECT_STICKERS_SET_ID = 'effectStickers';
export const EFFECT_EMOJIS_SET_ID = 'effectEmojis';
export const CHAT_STICKER_SET_ID = 'chatStickers';
export const DEFAULT_TOPIC_ICON_STICKER_ID = 'topic-default-icon';
export const DEFAULT_STATUS_ICON_ID = 'status-default-icon';
export const EMOJI_IMG_REGEX = /<img[^>]+alt="([^"]+)"(?![^>]*data-document-id)[^>]*>/gm;

export const FALLBACK_LANG_CODE = 'en';
export const BASE_EMOJI_KEYWORD_LANG = FALLBACK_LANG_CODE;

export const MENU_TRANSITION_DURATION = 200;
export const SLIDE_TRANSITION_DURATION = 450;

export const BIRTHDAY_NUMBERS_SET = 'FestiveFontEmoji';
export const RESTRICTED_EMOJI_SET = 'RestrictedEmoji';

export const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
export const SVG_EXTENSIONS = new Set(['svg', 'svgz']);

export const VIDEO_WEBM_TYPE = 'video/webm';
export const GIF_MIME_TYPE = 'image/gif';

export const LOTTIE_STICKER_MIME_TYPE = 'application/x-tgsticker';
export const VIDEO_STICKER_MIME_TYPE = VIDEO_WEBM_TYPE;

export const SUPPORTED_PHOTO_CONTENT_TYPES = new Set([
  'image/png', 'image/jpeg', GIF_MIME_TYPE,
]);

export const SUPPORTED_VIDEO_CONTENT_TYPES = new Set([
  'video/mp4', 'video/quicktime',
]);

export const SUPPORTED_AUDIO_CONTENT_TYPES = new Set([
  'audio/mp3',
  'audio/ogg',
  'audio/wav',
  'audio/mpeg',
  'audio/flac',
  'audio/aac',
  'audio/m4a',
  'audio/mp4',
  'audio/x-m4a',
]);

export const CONTENT_TYPES_WITH_PREVIEW = new Set([
  ...SUPPORTED_PHOTO_CONTENT_TYPES,
  ...SUPPORTED_VIDEO_CONTENT_TYPES,
]);

export const STORYBOARD_MIME = 'application/x-tgstoryboard';
export const STORYBOARD_MAP_MIME = 'application/x-tgstoryboardmap';

// Taken from https://github.com/telegramdesktop/tdesktop/blob/41d9a9fcbd0c809c60ddbd9350791b1436aff7d9/Telegram/SourceFiles/ui/boxes/choose_language_box.cpp#L28
export const SUPPORTED_TRANSLATION_LANGUAGES = [
  // Official
  'en', 'ar', 'be', 'ca', 'zh', 'nl', 'fr', 'de', 'id',
  'it', 'ja', 'ko', 'pl', 'pt', 'ru', 'es', 'uk',
  // Unofficial
  'af', 'sq', 'am', 'hy', 'az', 'eu', 'bn', 'bs', 'bg',
  'ceb', 'zh-CN', 'zh-TW', 'co', 'hr', 'cs', 'da', 'eo',
  'et', 'fi', 'fy', 'gl', 'ka', 'el', 'gu', 'ht', 'ha',
  'haw', 'he', 'iw', 'hi', 'hmn', 'hu', 'is', 'ig', 'ga',
  'jv', 'kn', 'kk', 'km', 'rw', 'ku', 'ky', 'lo', 'la',
  'lv', 'lt', 'lb', 'mk', 'mg', 'ms', 'ml', 'mt', 'mi',
  'mr', 'mn', 'my', 'ne', 'no', 'ny', 'or', 'ps', 'fa',
  'pa', 'ro', 'sm', 'gd', 'sr', 'st', 'sn', 'sd', 'si',
  'sk', 'sl', 'so', 'su', 'sw', 'sv', 'tl', 'tg', 'ta',
  'tt', 'te', 'th', 'tr', 'tk', 'ur', 'ug', 'uz', 'vi',
  'cy', 'xh', 'yi', 'yo', 'zu',
];

// Deviates from upstream: the host is a sequence of real DNS labels (each starts AND ends
// alphanumeric) instead of a character blob that contained `.` itself. That blob let a run of dots
// stand in for a label, so ordinary typing — "hmm...ok", "wait...what", "...name" — matched as a
// host and was rendered as a link (and fetched a link preview from the composer). Group 3 is still
// the host, incl. the optional userinfo that keeps `[text](someone@example.com)` mailto links working.
// eslint-disable-next-line @stylistic/max-len
export const RE_LINK_TEMPLATE = '((ftp|https?):\\/\\/)?((?:[^\\s/@]{1,256}@)?(?:[a-zA-Z0-9](?:[-a-zA-Z0-9]*[a-zA-Z0-9])?\\.)+[a-zA-Z][-a-zA-Z0-9]{1,62})\\b([-a-zA-Z0-9()@:%_+.,~#?&/=]*)';
export const RE_MENTION_TEMPLATE = '(@[\\w\\d_-]+)';
export const RE_OMNI_LINK = /^omni:(\/\/)?/i;
// Recognizes an `https://<subdomain?>.<host>` link as our own (chat/message/invite links). Built
// from `PRODUCTION_HOSTNAME` so it tracks whatever domain this build is deployed on, plus any host
// listed in `INTERNAL_LINK_HOSTS`: the backend mints invite links against ITS deployment's web
// domain, which is not the one a local (or cross-environment) build runs on — and a link we fail to
// recognize as ours is treated as an outside URL, so tapping an invite offered to open the site in
// a new tab instead of joining the group.
export const INTERNAL_LINK_HOSTS = [
  PRODUCTION_HOSTNAME,
  // The host this build is actually served from. A link the app mints itself (a call invite, an
  // invite link) points at its own origin, and on any deployment that isn't the production domain
  // — staging, a review build, a dev server — that link was not recognized as ours, so opening it
  // parsed to nothing and the invite did nothing at all.
  ...(typeof window !== 'undefined' && window.location?.hostname ? [window.location.hostname] : []),
  ...(process.env.INTERNAL_LINK_HOSTS || '').split(/[\s,]+/).filter(Boolean),
];
const ESCAPED_INTERNAL_HOSTS = INTERNAL_LINK_HOSTS
  .map((host) => host.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  .join('|');
export const RE_TME_LINK = new RegExp(
  `^(https?:\\/\\/)?([-a-zA-Z0-9@:%_+~#=]{1,32}\\.)?(${ESCAPED_INTERNAL_HOSTS})(?=[:/?#]|$)`, 'i',
);
export const TME_LINK_PREFIX = WEB_VERSION_BASE;
// A username is 5-32 of `a-z0-9_`, which is also what tells a real handle apart from a mention that
// addresses no one: `@all` is three characters and can never be claimed by an account, so a mention
// that fails this is not something there is a chat to open.
export const USERNAME_PATTERN = /^[a-zA-Z0-9_]{5,32}$/;

export const BOT_FATHER_USERNAME = 'botfather';
export const USERNAME_PURCHASE_ERROR = 'USERNAME_PURCHASE_AVAILABLE';
export const MESSAGE_ID_REQUIRED_ERROR = 'MESSAGE_ID_REQUIRED';
export const PURCHASE_USERNAME = 'auction';
export const ACCEPTABLE_USERNAME_ERRORS = new Set([USERNAME_PURCHASE_ERROR, 'USERNAME_INVALID']);
export const TME_WEB_DOMAINS = new Set([PRODUCTION_HOSTNAME]);
export const WEB_APP_PLATFORM = 'weba';
export const LANG_PACK = 'weba';

export const NSFW_RESTRICTION_REASON = 'sensitive';

// eslint-disable-next-line @stylistic/max-len
export const COUNTRIES_WITH_12H_TIME_FORMAT = new Set(['AU', 'BD', 'CA', 'CO', 'EG', 'HN', 'IE', 'IN', 'JO', 'MX', 'MY', 'NI', 'NZ', 'PH', 'PK', 'SA', 'SV', 'US']);

export const API_CHAT_TYPES = ['bots', 'channels', 'chats', 'users', 'groups'] as const;

export const HEART_REACTION: ApiReactionEmoji = {
  type: 'emoji',
  emoticon: '❤',
};

// MTProto constants
export const SERVICE_NOTIFICATIONS_USER_ID = '777000';
export const REPLIES_USER_ID = '1271266957'; // TODO For Test connection ID must be equal to 708513
export const VERIFICATION_CODES_USER_ID = '489000';
export const ANONYMOUS_USER_ID = '2666000';
export const RESTRICTED_EMOJI_SET_ID = '7173162320003080';
export const COCOON_EMOJI_ID = '5197252827247841976';
export const LOCAL_MESSAGES_LIMIT = 1e6; // 1M
export const CHANNEL_ID_BASE = 10n ** 12n;
export const DEFAULT_GIF_SEARCH_BOT_USERNAME = 'gif';
export const ALL_FOLDER_ID = 0;
export const ARCHIVED_FOLDER_ID = 1;
export const SAVED_FOLDER_ID = -1;
// A folder title is capped in the UI only — the backend stores whatever it is sent. Twelve was
// inherited from Telegram, which counts a Latin alphabet; a Vietnamese name ("Công việc quan trọng")
// runs past it, and a full field is destructive with an external IME (EVKey/UniKey): the keystrokes
// it is still buffering get swallowed, then its own backspaces land on text that is already there
// and eat it. The tab strip scrolls, so a longer title costs nothing but a wider tab.
export const FOLDER_TITLE_MAX_LENGTH = 32;
export const DELETED_COMMENTS_CHANNEL_ID = (-CHANNEL_ID_BASE - 777n).toString();
export const MAX_MEDIA_FILES_FOR_ALBUM = 10;
export const MAX_ACTIVE_PINNED_CHATS = 5;
export const SCHEDULED_WHEN_ONLINE = 0x7FFFFFFE;
export const LANG_PACKS = ['android', 'ios', 'tdesktop', 'macos'] as const;
// Product pages. Built from `PRODUCTION_URL` so a build always points at its own deployment
// instead of a hardcoded domain that isn't ours.
export const FEEDBACK_URL = `${PRODUCTION_URL}/feedback`;
export const FAQ_URL = `${PRODUCTION_URL}/faq`;
export const PRIVACY_URL = `${PRODUCTION_URL}/privacy`;
export const MINI_APP_TOS_URL = `${PRODUCTION_URL}/tos/mini-apps`;
export const FRAGMENT_ADS_URL = `${PRODUCTION_URL}/ads`;
export const GENERAL_TOPIC_ID = 1;
export const FRESH_AUTH_PERIOD = 86400; // 1 day
export const GIVEAWAY_BOOST_PER_PREMIUM = 4;
export const GIVEAWAY_MAX_ADDITIONAL_CHANNELS = 10;
export const GIVEAWAY_MAX_ADDITIONAL_USERS = 10;
export const GIVEAWAY_MAX_ADDITIONAL_COUNTRIES = 10;
export const FRAGMENT_PHONE_CODE = '888';
export const FRAGMENT_PHONE_LENGTH = 11;
export const BOT_VERIFICATION_PEERS_LIMIT = 20;

export const CALL_PROTOCOL_LIBRARY_VERSIONS = ['13.0.0'];

export const LIGHT_THEME_BG_COLOR = '#99BA92';
export const DARK_THEME_BG_COLOR = '#000000';
export const DEFAULT_PATTERN_COLOR = '#4A8E3A8C';
export const DARK_THEME_PATTERN_COLOR = '#48576166';
export const PEER_COLOR_BG_OPACITY = '1a';
export const PEER_COLOR_BG_ACTIVE_OPACITY = '2b';
export const PEER_COLOR_GRADIENT_STEP = 5; // px
export const MAX_UPLOAD_FILEPART_SIZE = 524288;

export const IGNORE_UNHANDLED_ERRORS = new Set([
  'USER_CANCELED',
]);

// Group calls
export const GROUP_CALL_VOLUME_MULTIPLIER = 100;
export const GROUP_CALL_DEFAULT_VOLUME = 100 * GROUP_CALL_VOLUME_MULTIPLIER;

// LiveKit calling (1:1 + group voice chat) over the backend token proxy.
// The browser never holds the call-service secret: it calls our backend's
// `POST /calls/room-token`, which authorizes the user and mints the LiveKit token
// server-side. This flag only gates the client UI; set it to `false` to keep the
// default TDLib call path. The backend independently disables the endpoint when its
// `CALL_GROUP_TOKEN_URL` is unset.
export const CALL_LIVEKIT_ENABLED = process.env.CALL_LIVEKIT_ENABLED === 'true';

// --- Feature rollout tier (core-first launch; see deploy/ROLLOUT_PLAN.md) ---
// `WEB_ROLLOUT_TIER=core` hides the Sprint-2 feature UI so a Sprint-1 build ships only the core
// chat product; default 'full' shows everything. Each flag also folds in its own prerequisite
// (e.g. calls still require LiveKit). Gate feature entry-point UI on `FEATURES.<x>`.
export const WEB_ROLLOUT_TIER = process.env.WEB_ROLLOUT_TIER === 'core' ? 'core' : 'full';
const IS_FULL_ROLLOUT = WEB_ROLLOUT_TIER === 'full';
// Money is switched off product-wide. Nothing that shows a price or asks this user to pay is
// rendered: no purchase, top-up, gift, boost, paid reaction or paid-message price. Money content
// somebody else sent (a gift bubble, an invoice, a giveaway post) still renders as before — only
// the controls that would spend this user's money are gone. Flip to `IS_FULL_ROLLOUT` to restore.
const ARE_PAYMENTS_ENABLED = false;

export const FEATURES = {
  calls: IS_FULL_ROLLOUT && CALL_LIVEKIT_ENABLED,
  // Every entry point that opens a purchase, a balance, a gift or a price
  payments: ARE_PAYMENTS_ENABLED,
  // Stars are fully backed (balance, history, top-up options, subscriptions), but the balance is a
  // wallet — it follows the payments switch
  stars: IS_FULL_ROLLOUT && ARE_PAYMENTS_ENABLED,
  // TON has no backend at all (`/ton/status` 404, `/stars/status` ignores the TON flag and always
  // answers in stars), so its balance row stays hidden rather than showing a fake zero
  ton: false,
  stickers: IS_FULL_ROLLOUT,
  reactions: IS_FULL_ROLLOUT,
  stories: IS_FULL_ROLLOUT,
  bots: IS_FULL_ROLLOUT,
  statistics: IS_FULL_ROLLOUT,
  // The backend gates every premium feature on its own `user.isPremium`, so the entry points can
  // ship. With payments off the promo is a read-only feature list, and `openPremiumModal` only
  // opens it for a subscriber — a non-subscriber has nothing to do there but look at a price.
  premium: IS_FULL_ROLLOUT,
};

// Dev-only override to preview premium-gated UI (custom-emoji picker, premium stickers, multi-
// reactions…) while the backend has premium off. Off by default; toggle via `SIMULATE_PREMIUM=true`
// in the env, NOT by faking a user's `isPremium`. When the backend ships premium it returns
// `isPremium` per user and everything follows automatically — no code change, keep this `false`.
export const IS_PREMIUM_SIMULATED = process.env.SIMULATE_PREMIUM === 'true';

export const ONE_TIME_MEDIA_TTL_SECONDS = 2147483647;

// Premium
export const PREMIUM_FEATURE_SECTIONS = [
  'stories',
  'double_limits',
  'more_upload',
  'faster_download',
  'voice_to_text',
  'no_ads',
  'infinite_reactions',
  'premium_stickers',
  'animated_emoji',
  'advanced_chat_management',
  'profile_badge',
  'animated_userpics',
  'emoji_status',
  'translations',
  'saved_tags',
  'last_seen',
  'message_privacy',
  'effects',
  'ai_compose',
  'todo',
  'pm_noforwards',
] as const;

export const PREMIUM_BOTTOM_VIDEOS: ApiPremiumSection[] = [
  'faster_download',
  'voice_to_text',
  'advanced_chat_management',
  'infinite_reactions',
  'profile_badge',
  'animated_userpics',
  'emoji_status',
  'translations',
  'saved_tags',
  'last_seen',
  'message_privacy',
  'effects',
  'todo',
];

export const PREMIUM_LIMITS_ORDER: ApiLimitTypeForPromo[] = [
  'channels',
  'dialogFolderPinned',
  'channelsPublic',
  'savedGifs',
  'stickersFaved',
  'aboutLength',
  'captionLength',
  'dialogFilters',
  'dialogFiltersChats',
  'moreAccounts',
  'recommendedChannels',
];

export const DEFAULT_GIFT_PROFILE_FILTER_OPTIONS: GiftProfileFilterOptions = {
  sortType: 'byDate',
  shouldIncludeUnlimited: true,
  shouldIncludeLimited: true,
  shouldIncludeUnique: true,
  shouldIncludeDisplayed: true,
  shouldIncludeHidden: true,
  shouldIncludeUpgradable: true,
} as const;

export const DEFAULT_RESALE_GIFTS_FILTER_OPTIONS: ResaleGiftsFilterOptions = {
  sortType: 'byDate',
};

export const ACCOUNT_TTL_OPTIONS = [1, 3, 6, 12, 18, 24];

export const SLOT_MACHINE_EMOJI = '🎰';
